import React from 'react';
import { styled, Box } from '@mui/material';

export interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  /** Form content */
  children: React.ReactNode;
  /** Form submission handler */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Space between form fields (in units of theme.spacing) */
  spacing?: number;
}

const FormRoot = styled('form')({
  width: '100%',
});

const FormContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'spacing',
})<{ spacing?: number }>(({ theme, spacing = 3 }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(spacing),
}));

export const Form: React.FC<FormProps> = ({
  children,
  onSubmit,
  spacing = 3,
  ...rest
}) => {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(e);
    }
  };

  return (
    <FormRoot onSubmit={handleSubmit} {...rest}>
      <FormContent spacing={spacing}>{children}</FormContent>
    </FormRoot>
  );
};

export interface FormRowProps {
  /** Row content */
  children: React.ReactNode;
  /** Horizontal space between form fields */
  spacing?: number;
}

const RowContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'spacing',
})<{ spacing?: number }>(({ theme, spacing = 2 }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(spacing),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

export const FormRow: React.FC<FormRowProps> = ({
  children,
  spacing = 2,
}) => {
  return <RowContent spacing={spacing}>{children}</RowContent>;
};

export default { Form, FormRow };
